import { observer } from 'mobx-react-lite';
import {
  capacityMessage,
  capacityUsed,
  unitMaxQuantity,
  UnitsNotice,
  UnitStepper
} from '@link/share/booking';
import type { CollapsiblePlan } from '@link/share/hooks/productPageSelectors';
import { resolveUnitAmount } from '@link/share/utils';
import { CAPABILITIES } from '../../../../capabilities';
import { useBookingStore } from '../context';
import styles from './plans.module.css';

interface Props {
  plan: CollapsiblePlan;
}

/**
 * How many of each ticket type, for the plan's selected day. The units and
 * their prices come from the calendar. Always the active plan: only its
 * fields are mounted.
 */
export const PlanUnits = observer(function PlanUnits({ plan }: Props) {
  const store = useBookingStore();
  const planId = plan.activityPlan.id ?? '';
  const selected = store.planStates.get(planId)?.selectedUnits ?? [];
  const quantities = new Map(
    selected.map(unit => [unit.id ?? '', unit.quantity ?? 0])
  );
  // The plan's maximum lowered to the selected slot's remaining allotment.
  const limits = store.capacity;
  const used = capacityUsed(plan.units, quantities);
  const notice = CAPABILITIES.capacityNotice
    ? capacityMessage(plan.units, used, limits)
    : undefined;

  const changeQuantity = (unitId: string, quantity: number) =>
    store.changeUnits(
      planId,
      plan.units.map(unit => ({
        ...unit,
        quantity: unit.id === unitId ? quantity : (quantities.get(unit.id) ?? 0)
      }))
    );

  if (!plan.units.length) return null;
  return (
    <div className={styles.units}>
      {notice && <UnitsNotice message={notice} />}
      {plan.units.map(unit => (
        <UnitStepper
          key={unit.id}
          name={unit.name}
          amount={resolveUnitAmount(unit, store.showOtherCurrencies)}
          currency={store.currency}
          quantity={quantities.get(unit.id) ?? 0}
          max={unitMaxQuantity(unit, used, quantities, limits)}
          onChange={quantity => changeQuantity(unit.id, quantity)}
        />
      ))}
    </div>
  );
});
